import { useState, useEffect, useEffectEvent } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Download,
  ExternalLink,
  Loader2,
  FileText,
  Image as ImageIcon,
  FileSpreadsheet,
  File,
  ZoomIn,
  ZoomOut,
  RotateCw,
} from 'lucide-react'
import { useObtenerUrlDocumento, useDescargarDocumento, CATEGORIAS_DOCUMENTO } from '@/core/hooks/useDocumentos'
import { formatFileSize } from '@/types/documentos'
import { toast } from 'sonner'
import { abrirEnlaceFirmado } from '@/lib/abrir-enlace'
import { cn } from '@/lib/utils'
import type { Documento } from '@/types/database'

interface DocumentoPreviewDialogProps {
  documento: Documento | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

type PreviewType = 'image' | 'pdf' | 'unsupported'

function getPreviewType(mimeType: string | null): PreviewType {
  if (!mimeType) return 'unsupported'
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType === 'application/pdf') return 'pdf'
  return 'unsupported'
}

function getDocumentoIcon(mimeType: string | null) {
  if (!mimeType) return <File className="h-16 w-16 text-gray-400" />
  if (mimeType.startsWith('image/')) return <ImageIcon className="h-16 w-16 text-blue-500" />
  if (mimeType === 'application/pdf') return <FileText className="h-16 w-16 text-red-500" />
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel'))
    return <FileSpreadsheet className="h-16 w-16 text-green-500" />
  if (mimeType.includes('word')) return <FileText className="h-16 w-16 text-blue-600" />
  return <File className="h-16 w-16 text-gray-400" />
}

export function DocumentoPreviewDialog({
  documento,
  open,
  onOpenChange,
}: DocumentoPreviewDialogProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isLoadingUrl, setIsLoadingUrl] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)

  const obtenerUrl = useObtenerUrlDocumento()
  const descargarDocumento = useDescargarDocumento()

  const previewType = documento ? getPreviewType(documento.mime_type) : 'unsupported'
  const categoriaConfig = documento?.categoria
    ? CATEGORIAS_DOCUMENTO[documento.categoria]
    : { label: 'Sin categoría', color: 'bg-gray-100 text-gray-800' }

  // Obtener URL cuando se abre el dialog
  const loadPreview = useEffectEvent(() => {
    if (open && documento && !previewUrl && !isLoadingUrl) {
      setIsLoadingUrl(true)
      setImageError(false)
      setZoom(1)
      setRotation(0)

      obtenerUrl
        .mutateAsync(documento.archivo_path)
        .then((url) => {
          setPreviewUrl(url)
        })
        .catch((error) => {
          console.error('Error al obtener URL:', error)
          toast.error('Error al cargar la vista previa')
        })
        .finally(() => {
          setIsLoadingUrl(false)
        })
    }
  })
  useEffect(() => {
    loadPreview()
  }, [open, documento])

  // Limpiar estado al cerrar
  const resetOnClose = useEffectEvent(() => {
    setPreviewUrl(null)
    setIsLoadingUrl(false)
    setImageError(false)
    setZoom(1)
    setRotation(0)
  })
  useEffect(() => {
    if (!open) resetOnClose()
  }, [open])

  const handleDescargar = async () => {
    if (!documento) return

    try {
      await descargarDocumento.mutateAsync({
        path: documento.archivo_path,
        nombreArchivo: documento.nombre_original,
      })
      toast.success('Descarga iniciada')
    } catch (error) {
      console.error('Error al descargar:', error)
      toast.error('Error al descargar el documento')
    }
  }

  // La URL de la vista previa puede haber vencido: la pestaña nueva pide la suya.
  const handleAbrirEnNuevaPestana = async () => {
    if (!documento) return
    try {
      const estado = await abrirEnlaceFirmado(() => obtenerUrl.mutateAsync(documento.archivo_path))
      if (estado === 'bloqueada') toast.error('El navegador bloqueó la pestaña nueva')
    } catch {
      toast.error('Error al abrir el documento')
    }
  }

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.25, 3))
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.25, 0.5))
  const handleRotate = () => setRotation((r) => (r + 90) % 360)

  if (!documento) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogDescription className="sr-only">
            Vista previa del documento {documento.nombre}
          </DialogDescription>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <DialogTitle className="truncate pr-8">{documento.nombre}</DialogTitle>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                <span>{formatFileSize(documento.archivo_size)}</span>
                <span>•</span>
                <Badge variant="secondary" className={cn('text-xs', categoriaConfig.color)}>
                  {categoriaConfig.label}
                </Badge>
                {documento.fecha_vencimiento && (
                  <>
                    <span>•</span>
                    <span>
                      Vence: {new Date(documento.fecha_vencimiento).toLocaleDateString('es-AR')}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Acciones del header */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAbrirEnNuevaPestana}
                disabled={!previewUrl}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Abrir
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDescargar}
                disabled={descargarDocumento.isPending}
              >
                {descargarDocumento.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Contenido de vista previa */}
        <div className="flex-1 overflow-hidden relative bg-muted/30">
          {isLoadingUrl ? (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Cargando vista previa...</p>
            </div>
          ) : previewType === 'image' && previewUrl && !imageError ? (
            <>
              {/* Controles de zoom para imágenes */}
              <div className="absolute top-4 right-4 z-10 flex items-center gap-1 bg-background/90 backdrop-blur rounded-lg p-1 shadow-sm">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleZoomOut}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleZoomIn}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <div className="w-px h-4 bg-border mx-1" />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleRotate}>
                  <RotateCw className="h-4 w-4" />
                </Button>
              </div>

              {/* Imagen con zoom y rotación */}
              <div className="h-full w-full overflow-auto flex items-center justify-center p-4">
                <img
                  src={previewUrl}
                  alt={documento.nombre}
                  className="max-w-none transition-transform duration-200"
                  style={{
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    maxHeight: zoom === 1 ? '100%' : 'none',
                    maxWidth: zoom === 1 ? '100%' : 'none',
                  }}
                  onError={() => setImageError(true)}
                />
              </div>
            </>
          ) : previewType === 'pdf' && previewUrl ? (
            <iframe
              src={`${previewUrl}#toolbar=1&navpanes=0`}
              className="w-full h-full border-0"
              title={documento.nombre}
            />
          ) : (
            /* Vista para archivos no soportados o error de imagen */
            <div className="flex flex-col items-center justify-center h-full gap-4 p-6">
              {getDocumentoIcon(documento.mime_type)}
              <div className="text-center">
                <p className="font-medium text-lg">{documento.nombre}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {imageError
                    ? 'No se pudo cargar la imagen'
                    : 'Vista previa no disponible para este tipo de archivo'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {documento.mime_type || 'Tipo desconocido'} • {formatFileSize(documento.archivo_size)}
                </p>
              </div>
              <div className="flex gap-3 mt-2">
                <Button onClick={handleDescargar} disabled={descargarDocumento.isPending}>
                  {descargarDocumento.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Descargar
                </Button>
                <Button variant="outline" onClick={handleAbrirEnNuevaPestana} disabled={!previewUrl}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Abrir en nueva pestaña
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer con info adicional */}
        {documento.nombre !== documento.nombre_original && (
          <div className="px-6 py-3 border-t bg-muted/30 text-xs text-muted-foreground shrink-0">
            Archivo original: {documento.nombre_original}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
